import { IconButton } from '@creditornot/cb-button';
import { CaretDown, CaretUp, Cross } from '@creditornot/cb-icons';
import { colors, cbSemanticCssVars, Elevation } from '@creditornot/cb-ingredients';
import { Tag } from '@creditornot/cb-tag';
import { DateTime, Interval } from 'luxon';
import R from 'ramda';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Typography as T } from 'common/typography';

import SpecialHoursDatePicker from './SpecialHoursDatePicker';
import { DatePeriod } from './types';

type Props = {
  className?: string;
  periods: DatePeriod[];
  timezone: string;
  onChange: (periods: DatePeriod[]) => void;
  onDateModeChange?: (pickerMode: 'single' | 'period') => void;
};

const Root = styled.div`
  position: relative;
`;

const Toggle = styled.div<{ open: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: ${({ open }) => (open ? `1px solid ${colors.wolt100}` : `1px solid ${cbSemanticCssVars.borderPrimary}`)};
  border-radius: 8px;
  min-height: 40px;
  cursor: pointer;
`;

const Placeholder = styled(T.Body3)`
  color: ${cbSemanticCssVars.textSecondary};
  margin-inline-start: 12px;
`;

const Value = styled(Tag).attrs({
  variant: 'lightBlue',
})`
  margin: 6px 2px 6px 6px;
`;

const Values = styled.div``;

const Icons = styled.div`
  display: flex;
  padding-inline-end: 4px;
`;

const ClearIconButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'grey',
  background: 'staticWhite',
  alignment: 'boundingBox',
})`
  margin-inline-end: 8px;
`;

const ClearIcon = styled(Cross).attrs({})``;

const ArrowIconButton = styled(IconButton).attrs({
  size: 'tiny',
  variant: 'grey',
  background: 'staticWhite',
  alignment: 'boundingBox',
})``;

const DownArrowIcon = styled(CaretDown).attrs({})``;
const UpArrowIcon = styled(CaretUp).attrs({})``;

const DatePeriodPickerContainer = styled(Elevation).attrs({ level: 8 })`
  position: absolute;
  top: calc(100% + 8px);
  width: 100%;
  border-radius: 8px;
  z-index: 3;
  display: inline-block;
`;

const dateTimeToString = (dateTime: DateTime) => {
  return dateTime.toLocaleString({ year: '2-digit', month: 'numeric', day: 'numeric' });
};

const formatDateRange = (start: DateTime, end: DateTime) => {
  if (Interval.fromDateTimes(start, end).length('days') === 0) {
    return dateTimeToString(start);
  }
  return `${dateTimeToString(start)}–${dateTimeToString(end)} `;
};

const SpecialHoursDateSelect = ({ className, periods, timezone, onChange, onDateModeChange }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const componentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    window.addEventListener('click', handleOutsideClick, true);

    return () => window.removeEventListener('click', handleOutsideClick, true);
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDatePickerChange = (newPeriods: DatePeriod[]) => {
    onChange(newPeriods);
    setIsOpen(false);
  };

  return (
    <Root className={className} ref={componentRef}>
      <Toggle
        onClick={() => {
          setIsOpen(!isOpen);
        }}
        open={isOpen}
      >
        <Values>
          {periods.length ? (
            periods.map((period, index) => {
              return (
                <Value
                  key={index}
                  onRemoveClick={event => {
                    event.stopPropagation();
                    onChange(R.remove(index, 1, periods));
                  }}
                >
                  {formatDateRange(period.start, period.end)}
                </Value>
              );
            })
          ) : (
            <Placeholder>
              <FormattedMessage id="opening-hours.days" defaultMessage="Days" />
            </Placeholder>
          )}
        </Values>
        <Icons>
          {!!periods.length && (
            <ClearIconButton
              onClick={e => {
                // Prevent submit if used inside <form> and prevent
                // the dropdown from opening or closing when clicked.
                e.stopPropagation();
                onChange([]);
              }}
            >
              <ClearIcon />
            </ClearIconButton>
          )}
          <ArrowIconButton>{isOpen ? <UpArrowIcon /> : <DownArrowIcon />}</ArrowIconButton>
        </Icons>
      </Toggle>
      {isOpen && (
        <DatePeriodPickerContainer>
          <SpecialHoursDatePicker
            value={periods}
            timezone={timezone}
            onChange={handleDatePickerChange}
            onDateModeChange={onDateModeChange}
          />
        </DatePeriodPickerContainer>
      )}
    </Root>
  );
};

export default SpecialHoursDateSelect;
